/**
 * ASCII Art Generator - 波浪效果计算
 *
 * 实现正弦波动画，使字符位置产生波浪偏移效果
 */

import type { WaveConfig } from './types'

/**
 * 计算波浪偏移量
 *
 * @param x 字符 X 坐标
 * @param y 字符 Y 坐标
 * @param time 当前时间 (秒)
 * @param config 波浪配置参数
 * @returns 偏移量 { offsetX, offsetY }
 */
export function calculateWaveOffset(
  x: number,
  y: number,
  time: number,
  config: WaveConfig
): { offsetX: number; offsetY: number } {
  const { amplitude, frequency, speed, direction = 'both' } = config

  // 波浪相位随时间变化
  const wavePhase = time * speed

  // 根据方向计算偏移
  let offsetX = 0
  let offsetY = 0

  switch (direction) {
    case 'horizontal':
      // 水平波浪：Y 方向偏移，基于 X 坐标
      offsetY = amplitude * Math.sin(frequency * (x * 0.1) + wavePhase)
      break

    case 'vertical':
      // 垂直波浪：X 方向偏移，基于 Y 坐标
      offsetX = amplitude * Math.sin(frequency * (y * 0.1) + wavePhase)
      break

    case 'both':
      // 双向波浪：两个方向的偏移叠加
      offsetX = amplitude * Math.sin(frequency * (y * 0.1) + wavePhase)
      offsetY = amplitude * Math.sin(frequency * (x * 0.1) + wavePhase + Math.PI / 4)
      break
  }

  return { offsetX, offsetY }
}

/**
 * 波浪效果类
 *
 * 提供波浪动画的状态管理和计算
 */
export class WaveEffect {
  private config: WaveConfig
  private time = 0

  constructor(config: WaveConfig) {
    this.config = { ...config }
  }

  /**
   * 更新时间（每帧调用）
   */
  update(deltaTime: number): void {
    this.time += deltaTime
  }

  /**
   * 获取当前时间
   */
  getTime(): number {
    return this.time
  }

  /**
   * 计算指定位置的波浪偏移
   */
  getOffset(x: number, y: number): { offsetX: number; offsetY: number } {
    return calculateWaveOffset(x, y, this.time, this.config)
  }

  /**
   * 更新配置
   */
  setConfig(config: Partial<WaveConfig>): void {
    this.config = { ...this.config, ...config }
  }

  /**
   * 获取当前配置
   */
  getConfig(): WaveConfig {
    return { ...this.config }
  }

  /**
   * 重置时间
   */
  reset(): void {
    this.time = 0
  }
}

/**
 * 从渲染设置创建波浪配置
 */
export function createWaveConfigFromSettings(
  amplitude: number,
  frequency: number,
  speed: number,
  charWidth: number = 10,
  charHeight: number = 10
): WaveConfig {
  // 将相对参数转换为像素偏移
  // amplitude: 0-1 范围，转换为 0-2倍字符高度（更明显的效果）
  const pixelAmplitude = amplitude * Math.max(charWidth, charHeight) * 2

  // speed: 需要放大以产生可见动画效果
  // 原始速度 0.05 对应约 3 弧度/秒的相位变化
  const adjustedSpeed = speed * 60 // 放大60倍使动画更明显

  return {
    amplitude: pixelAmplitude,
    frequency,
    speed: adjustedSpeed,
    direction: 'both'
  }
}